import { createEquippedIntegerCompiler } from './equipped-integer-contribution';
import { createEnchantmentCompilationContext } from './enchantment-template-context';
import { createFromBaseNpcGrantContext } from './from-base-npc-stages';
import { readInventoryField, readItemUuidSlot } from './inventory-raw-readers';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';
import { fromBaseRecord, fromBaseUuid, FromBaseInputMissing } from './from-base-context';
import type { FromBaseRow as Row } from './from-base-context';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import type { ItemFieldRow } from './item-field-reader';
import { phpJsonString, phpJsonTruthy } from './php-json-values';

/** Native ft_gear_passive_counted_on_equipped_row: a passive flattened on its
 * equipped item must not add the same AC/mitigation again at the creature root. */
export function createNpcGearPassiveGuard(base: Row, metadata: FromBasePlayerResourceMetadata, host: Row) {
    const { common, definition, readers } = createFromBaseNpcGrantContext(base, metadata, host);
    const equipped = createEquippedIntegerCompiler({ ...common, metadata: metadata.preliminary, queryFullItem: definition });
    const entries = (row: ItemFieldRow | null, key: string) => Object.values(readers.layerEntries(row, key));
    const templates = new Map<string, Set<string>>();
    const enchantment = createEnchantmentCompilationContext({ ...common,
        dynamicLayers: metadata.inventoryMetadata!.dynamicLayers, spellCosts: metadata.inventoryMetadata!.spellCosts,
        templateRows: id => {
            if (!Array.isArray(base.enchantment_templates)) throw new FromBaseInputMissing('enchantment-template-sources', id);
            return base.enchantment_templates.filter(row => readers.itemField(row, 'enchantment_template_id') === id);
        }, memberRows: id => {
            if (!Array.isArray(base.enchantment_template_enchantments)) throw new FromBaseInputMissing('enchantment-member-sources', id);
            return base.enchantment_template_enchantments.filter(row => readers.itemField(row, 'enchantment_template_id') === id)
                .map(row => ({ ...row, item_name: readers.itemField(definition(phpJsonString(readers.itemField(row, 'enchantment_item_id'))), 'item_name') }));
        }, fullItem: definition });
    return (row: ItemFieldRow, entry: ItemFieldRow, layer: string): boolean => {
        if (!['ac_mod_magical', 'ac_mod_non_magical', 'damage_mitigation'].includes(layer)) return false;
        const source = readItemUuidSlot(readers.layerValue(entry, 'source'));
        const passive = readAbilityEntryId(entry, { ...readers, resolveCarrierRef: definition });
        if (!fromBaseUuid(source) || !fromBaseUuid(passive)) return false;
        for (const gear of equipped.collect(row)) {
            if (phpJsonString(readInventoryField(gear, 'inventory_id')).toUpperCase() !== source.toUpperCase()) continue;
            const computed = fromBaseRecord(gear) && fromBaseRecord(gear.computed) ? gear.computed : {};
            const nested = fromBaseRecord(computed.enchantment) && fromBaseRecord(computed.enchantment.abilities) ? computed.enchantment.abilities : {};
            if (Object.values(nested.passive_ability ?? {}).some(p => fromBaseRecord(p)
                && readers.layerValue(p, 'passive_conditions_active') !== false
                && phpJsonString(readAbilityEntryId(p, { ...readers, resolveCarrierRef: definition })).toUpperCase() === passive.toUpperCase())) return true;
            let carrier = gear, template = readers.layerValue(gear, 'enchantment_template_applied');
            if (!phpJsonTruthy(template)) {
                carrier = definition(readItemUuidSlot(readInventoryField(gear, 'inventory_item_id'))) ?? {};
                template = readers.layerValue(carrier, 'enchantment_template_applied');
            }
            if (!entries(carrier, layer).length) return false;
            const id = readItemUuidSlot(template).toUpperCase();
            if (!fromBaseUuid(id)) return false;
            if (!templates.has(id)) {
                const built = enchantment.buildTemplate(id, true);
                const abilities = fromBaseRecord(built) && fromBaseRecord(built.nested_abilities) ? built.nested_abilities : {};
                templates.set(id, new Set(Object.values(abilities.passive_ability ?? {}).flatMap(p =>
                    fromBaseRecord(p) && fromBaseRecord(p.item_arr) && readers.layerValue(p.item_arr, 'requires_equipped_item') === null
                        && !entries(p.item_arr, 'requires_equipped_item').length ? [phpJsonString(readers.itemField(p.item_arr, 'item_id')).toUpperCase()] : [])));
            }
            return templates.get(id)!.has(passive.toUpperCase());
        }
        return false;
    };
}
